import { useCallback } from 'react';

import { type EditInCanvasSubmenuOpenedFrom, useTrackEditInCanvasSubmenuShown } from '@air/analytics';
import { type Clip } from '@air/api/types';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { EditInCanvasSubmenuShownTracker } from '~/components/Canvas/EditInCanvasSubmenuShownTracker';
import { usePrivateSavedSizesCatalogContext } from '~/components/v2/components/saved-settings/PrivateSavedSizesCatalogProvider';
import { type PendingCanvasEditRequest } from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { getEditInCanvasOpenedAction } from '~/utils/canvases/getEditInCanvasAnalytics';
import {
  type EditInCanvasSizeSetExposure,
  getEditInCanvasEligibleSizeSet,
  getEditInCanvasSizeSetExposure,
} from '~/utils/canvases/getEditInCanvasSizeSetExposure';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import { getEditInCanvasSubItems } from '~/utils/menuOptions/getEditInCanvasSubItems';
import { isFirstSubmenuOpenSincePointerDown } from '~/utils/menuOptions/isFirstSubmenuOpenSincePointerDown';

export type EditInCanvasSelection = PendingCanvasEditRequest & {
  sizeSetExposure: EditInCanvasSizeSetExposure;
};

export type EditInCanvasMenuHandlers = {
  /** Whether this menu may offer "Resize for [set name]". Selection menus and the selection bar may not. */
  canOfferSizeSet: boolean;
  onSelect: (selection: EditInCanvasSelection) => void;
  openedFrom: EditInCanvasSubmenuOpenedFrom;
};

export type GetEditInCanvasMenuOptionParams = EditInCanvasMenuHandlers & {
  /** The clips the submenu acts on (already filtered to ones that can open in canvas). */
  clips: Clip[];
};

export const EDIT_IN_CANVAS_SUBMENU_SHOWN_TRACKER_ID = 'edit-in-canvas-submenu-shown-tracker';

/**
 * Builds the "Edit in Canvas" submenu for private asset menus, with its exposure analytics. The
 * submenu is reported shown by a tracker option that mounts with the sub options.
 */
export const usePrivateEditInCanvasMenuOption = () => {
  const { editInCanvasSizeSet: sizeSet } = usePrivateSavedSizesCatalogContext();
  const { editInCanvasResizeForSetEnabled } = useTemporaryFeatureFlags();
  const { trackEditInCanvasSubmenuShown } = useTrackEditInCanvasSubmenuShown();

  const getEditInCanvasMenuOption = useCallback(
    ({ canOfferSizeSet, clips, onSelect, openedFrom }: GetEditInCanvasMenuOptionParams): DropdownMenuOption => {
      const eligibleSizeSet = getEditInCanvasEligibleSizeSet({ canOfferSizeSet, clips, sizeSet });
      const subItems = getEditInCanvasSubItems({
        clips,
        offeredSizeSet: editInCanvasResizeForSetEnabled ? eligibleSizeSet : null,
        // `sizeSetExposure` needs the built items, so it is declared below; items are only selected later.
        onSelect: (request) => onSelect({ ...request, sizeSetExposure }),
      });
      const sizeSetExposure = getEditInCanvasSizeSetExposure({
        eligibleSizeSet,
        isOffered: subItems.actions.includes('resizeForSet'),
      });

      const onSubmenuShown = () => {
        const clipIds = clips.map((clip) => clip.id).join(':');

        if (!isFirstSubmenuOpenSincePointerDown(`edit-in-canvas:${openedFrom}:${clipIds}`)) {
          return;
        }

        trackEditInCanvasSubmenuShown({
          clip_count: clips.length,
          opened_from: openedFrom,
          tool_actions: subItems.actions.map(getEditInCanvasOpenedAction),
          ...sizeSetExposure,
        });
      };

      return {
        ...getDropdownMenuOption('edit-in-canvas'),
        options: [
          {
            children: <EditInCanvasSubmenuShownTracker onShown={onSubmenuShown} />,
            id: EDIT_IN_CANVAS_SUBMENU_SHOWN_TRACKER_ID,
            type: 'children',
          },
          ...subItems.options,
        ],
        type: 'sub',
      };
    },
    [editInCanvasResizeForSetEnabled, sizeSet, trackEditInCanvasSubmenuShown],
  );

  return { getEditInCanvasMenuOption };
};
